// FILE: ui/services/dailySummaryService.ts
// 目的：提供儀錶板（Shell / useTodosLiveMonitor）需要的型別與「今日摘要」讀取工具。
// 註：目前儀錶板主要以「即時計算」為主；此檔仍保留讀取 dashboard_daily_summary 的能力，
//     並做新/舊 schema 的相容解析，避免資料版本不同時整頁爆紅。

import { db, doc, getDoc } from "../../firebaseClient";
import { formatDate } from "../utils/dateUtils";
import { getRandomWarmTip } from "../utils/getRandomWarmTip";

// --- Core Types ---

export type SummaryUserKey = "kevin" | "ops" | "gm";

export type PendingModule =
  | "contractReviews"
  | "templates"
  | "seals"
  | "cases"
  | "govCorrespondence"
  | "regIntel"
  | "projectControl"
  | "governanceWorkspace";

export interface SummaryCounts {
  contractReviews: number;
  templates: number;
  seals: number;
  cases: number;
  govCorrespondence: number;
  regIntel: number;
  projectControl: number;
  governanceWorkspace: number;
  total: number;
}

export interface PendingItem {
  id: string;
  title: string;
  module: PendingModule;

  // --- Debug/trace fields (optional) ---
  docNo?: string;
  source?: string;
  rawId?: string;
  moduleKey?: string;
  status?: string;
  stageKey?: string;
  targetUserKeys?: SummaryUserKey[];
}

export interface PendingGrouped {
  contractReviews: PendingItem[];
  templates: PendingItem[];
  seals: PendingItem[];
  cases: PendingItem[];
  govCorrespondence: PendingItem[];
  regIntel: PendingItem[];
  projectControl: PendingItem[];
  governanceWorkspace: PendingItem[];
}

export interface DueItem {
  title: string;
  date: string;
  type: string;
}

export interface DueSoonGroup {
  today: DueItem[];
  within30: DueItem[];
  within60: DueItem[];
}

export interface UserRealtimeSummary {
  userKey: SummaryUserKey;
  displayName: string;
  counts: SummaryCounts;
  dateLabel: string;
  pendingList: PendingGrouped;
}

export interface DailySummaryResult {
  dateKey: string;
  dateLabel: string;
  generatedAt: string;
  users: UserRealtimeSummary[];
  dueSoon: DueSoonGroup;
  warmTip?: string;

  // Debug payload（選用）
  debug?: {
    schemaVersion?: string;
    raw?: any;
  };
}

export function emptyPendingGrouped(): PendingGrouped {
  return {
    contractReviews: [],
    templates: [],
    seals: [],
    cases: [],
    govCorrespondence: [],
    regIntel: [],
    projectControl: [],
    governanceWorkspace: [],
  };
}

export function computeCounts(p: PendingGrouped): SummaryCounts {
  const contractReviews = p.contractReviews.length;
  const templates = p.templates.length;
  const seals = p.seals.length;
  const cases = p.cases.length;
  const govCorrespondence = p.govCorrespondence.length;
  const regIntel = p.regIntel.length;
  const projectControl = p.projectControl.length;
  const governanceWorkspace = p.governanceWorkspace.length;
  const total =
    contractReviews +
    templates +
    seals +
    cases +
    govCorrespondence +
    regIntel +
    projectControl +
    governanceWorkspace;
  return {
    contractReviews,
    templates,
    seals,
    cases,
    govCorrespondence,
    regIntel,
    projectControl,
    governanceWorkspace,
    total,
  };
}

export function buildEmptyUser(userKey: SummaryUserKey, displayName: string, dateLabel: string): UserRealtimeSummary {
  const pendingList = emptyPendingGrouped();
  return {
    userKey,
    displayName,
    dateLabel,
    pendingList,
    counts: computeCounts(pendingList),
  };
}

function asUserKey(k: any): SummaryUserKey | null {
  const v = String(k || "").trim().toLowerCase();
  if (v === "kevin") return "kevin";
  if (v === "ops") return "ops";
  if (v === "gm") return "gm";
  return null;
}

function normalizeLegacyModule(m: any): PendingModule {
  const v = String(m || "").trim();
  const lower = v.toLowerCase();

  // 新 schema（本專案 8 分類）
  if (
    [
      "contractreviews",
      "templates",
      "seals",
      "cases",
      "govcorrespondence",
      "regintel",
      "projectcontrol",
      "governanceworkspace",
    ].includes(lower)
  ) {
    return lower as PendingModule;
  }

  // 舊 schema：contract / seal / case / template / governance / other
  if (lower === "contract") return "contractReviews";
  if (lower === "seal") return "seals";
  if (lower === "template") return "templates";
  if (lower === "governance") return "governanceWorkspace";
  if (lower === "case") return "cases";

  // 「other」一律落回 cases（移除『其他』分組）
  return "cases";
}

function pushPending(grouped: PendingGrouped, item: PendingItem) {
  (grouped[item.module] ||= []).push(item);
}

function coercePendingItem(raw: any): PendingItem | null {
  if (!raw) return null;
  const id = String(raw.id || raw.rawId || raw.docNo || "").trim();
  const title = String(raw.title || raw.name || raw.subject || "(未命名)").trim();
  if (!id) return null;

  const module = normalizeLegacyModule(raw.module);
  const targetUserKeys = Array.isArray(raw.targetUserKeys)
    ? raw.targetUserKeys.map(asUserKey).filter(Boolean)
    : undefined;

  return {
    id,
    title,
    module,
    docNo: raw.docNo,
    source: raw.source,
    rawId: raw.rawId,
    moduleKey: raw.moduleKey,
    status: raw.status,
    stageKey: raw.stageKey,
    targetUserKeys: (targetUserKeys as SummaryUserKey[]) || undefined,
  };
}

function normalizeUserDisplayName(k: SummaryUserKey): string {
  if (k === "kevin") return "法務";
  if (k === "ops") return "營運長";
  return "總經理";
}

function emptyResult(today: string): DailySummaryResult {
  const dateLabel = today;
  return {
    dateKey: today,
    dateLabel,
    generatedAt: new Date().toISOString(),
    users: [
      buildEmptyUser("kevin", normalizeUserDisplayName("kevin"), dateLabel),
      buildEmptyUser("ops", normalizeUserDisplayName("ops"), dateLabel),
      buildEmptyUser("gm", normalizeUserDisplayName("gm"), dateLabel),
    ],
    dueSoon: { today: [], within30: [], within60: [] },
    warmTip: getRandomWarmTip(),
    debug: { schemaVersion: "empty" },
  };
}

/**
 * 讀取 dashboard_daily_summary/<YYYY-MM-DD>。
 * - 支援舊版 schema（contracts/seals/others + legacy modules）
 * - 支援新版 schema（8 分類）
 * 若資料不存在，回傳空摘要（避免 UI 報錯）。
 */
export async function getTodaySummary(date?: Date): Promise<DailySummaryResult> {
  const today = formatDate(date || new Date());
  const res = emptyResult(today);

  try {
    const ref = doc(db as any, "dashboard_daily_summary", today);
    const snap = await getDoc(ref as any);
    if (!snap.exists()) return res;

    const data: any = snap.data() || {};
    const rawUsers = data.users || {};

    // users 可能是 object（以 userKey 為 key）或 array
    const normalizedMap: Record<SummaryUserKey, any> = {
      kevin: null,
      ops: null,
      gm: null,
    };

    if (Array.isArray(rawUsers)) {
      for (const u of rawUsers) {
        const k = asUserKey(u?.userKey);
        if (k) normalizedMap[k] = u;
      }
    } else {
      for (const k of Object.keys(rawUsers || {})) {
        const uk = asUserKey(k);
        if (uk) normalizedMap[uk] = rawUsers[k];
      }
    }

    const dateLabel = String(data.dateLabel || res.dateLabel);
    const users: UserRealtimeSummary[] = [];

    (Object.keys(normalizedMap) as SummaryUserKey[]).forEach((k) => {
      const raw = normalizedMap[k] || {};
      const displayName = String(raw.displayName || normalizeUserDisplayName(k));

      const grouped = emptyPendingGrouped();

      // pendingList 可能是：
      // - 新版：{ contractReviews:[], ... }
      // - 舊版：{ contracts:[], seals:[], cases:[], templates:[], governance:[], others:[] }
      const pl = raw.pendingList || raw.pending || {};

      if (pl && typeof pl === "object") {
        const keys = Object.keys(pl);
        // 1) 新版：逐欄塞
        for (const kk of keys) {
          const lower = kk.toLowerCase();
          const arr = Array.isArray(pl[kk]) ? pl[kk] : [];

          // 舊欄位名轉新欄位名
          let target: PendingModule;
          if (lower === "contracts") target = "contractReviews";
          else if (lower === "seals") target = "seals";
          else if (lower === "cases") target = "cases";
          else if (lower === "templates") target = "templates";
          else if (lower === "governance") target = "governanceWorkspace";
          else if (lower === "others") target = "cases";
          else {
            // 新版欄位名
            target = normalizeLegacyModule(lower);
          }

          for (const it of arr) {
            const item = coercePendingItem({ ...it, module: it?.module ?? target, id: it?.id ?? it?.rawId ?? it?.docNo });
            if (!item) continue;
            // 以 item.module 為準（normalizeLegacyModule 會把 other 歸入 cases）
            pushPending(grouped, item);
          }
        }
      }

      const counts = computeCounts(grouped);

      users.push({
        userKey: k,
        displayName,
        dateLabel,
        pendingList: grouped,
        counts,
      });
    });

    return {
      ...res,
      dateLabel,
      generatedAt: String(data.generatedAt || res.generatedAt),
      warmTip: String(data.warmTip || res.warmTip || getRandomWarmTip()),
      dueSoon: (data.dueSoon as any) || res.dueSoon,
      users,
      debug: { schemaVersion: String(data.schemaVersion || "unknown"), raw: data },
    };
  } catch (e) {
    // 任何錯誤都回空摘要，避免整頁掛掉
    return res;
  }
}
